import { ReactNode } from 'react';
import { FundingPolicyType } from '@shared/appModel';
import { CollapsibleSection } from '../CollapsibleSection';
import { TooltipLabel } from '../TooltipLabel';
import { currency } from '../projectionTable/formatters';
import { useAccumulationTimeline } from './useAccumulationTimeline';

// A one-line preview for the collapsed accordion trigger: the years of growth
// plus the headline balance's start → care-start values. Picks the first
// balance that actually grows (pool, else monthly benefit, else death benefit)
// so flat-pool products (e.g. CareMatters II, whose growth is in the rider)
// still show a meaningful change. The dollar figures are slightly bolded to
// match the highlight stats in the other section headers.
function buildSummary(timeline: AccumulationYear[], terms: ClaimTerms): ReactNode {
  const years = timeline.length - 1;
  const first = timeline[0].snapshot;
  const last = timeline[timeline.length - 1].snapshot;
  const grew = (a: number, b: number) => b - a > 0.5;

  const candidates: Array<{ label: string; a: number; b: number }> = [];
  if (!terms.isUnlimited) {
    candidates.push({
      label: 'LTC pool',
      a: first.theoreticalLtcPoolCeiling,
      b: last.theoreticalLtcPoolCeiling,
    });
  }
  candidates.push({ label: 'Monthly benefit', a: first.monthlyBenefitCap, b: last.monthlyBenefitCap });
  candidates.push({ label: 'Death benefit', a: first.effectiveFaceAmount, b: last.effectiveFaceAmount });

  const headline = candidates.find(c => grew(c.a, c.b));
  if (!headline) return `${years} yrs to care`;
  return (
    <>
      {years} yrs · {headline.label} <Figure>{currency(headline.a)}</Figure> →{' '}
      <Figure>{currency(headline.b)}</Figure>
    </>
  );
}

function Figure({ children }: { children: ReactNode }) {
  return <span className="font-semibold tabular-nums">{children}</span>;
}

interface AccumulationTimelineSectionProps {
  projection: ClaimProjection;
  policyType: FundingPolicyType | null | undefined;
}

/**
 * Pre-care growth section: a year-by-year table of how each policy balance
 * (LTC pool, monthly benefit, death benefit / contract value) grew from policy
 * issue through the year long-term care begins. Rendered above the month table
 * so the reader has the accumulation context before any month row. Hidden when
 * there is no accumulation phase (care begins in policy year 1, or a pre-policy
 * claim).
 */
export function AccumulationTimelineSection({
  projection,
  policyType,
}: AccumulationTimelineSectionProps) {
  const { timeline, columns } = useAccumulationTimeline(projection, policyType);

  // Need at least two years (issue → care start) and a balance column beyond
  // the sticky "Policy year" column to be worth showing.
  if (timeline.length < 2 || columns.length < 2) return null;

  const careStartYear = timeline[timeline.length - 1];
  const subtitle = buildSummary(timeline, projection.terms);

  return (
    <CollapsibleSection title="Policy Growth" subtitle={subtitle} defaultOpen={false}>
      <p className="mb-3 text-xs text-gray-500">
        How the policy&rsquo;s values grow from issue ({timeline[0].calendarYear}) until
        long-term care is projected to begin ({careStartYear.calendarYear}), before any
        benefits are used. Hover a value to see how it grew.
      </p>
      <div className="overflow-auto rounded-md border border-gray-200">
        <table className="min-w-full border-collapse text-xs">
          <thead className="bg-gray-50">
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`whitespace-nowrap border-b border-gray-200 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700 ${
                    col.sticky ? 'sticky left-0 z-10 bg-gray-50' : ''
                  }`}
                >
                  {col.tooltip ? (
                    <TooltipLabel label={col.label} tooltip={col.tooltip} />
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeline.map((row, i) => {
              const prev = i > 0 ? timeline[i - 1] : null;
              return (
                <tr
                  key={row.policyYear}
                  className={`border-b border-gray-100 hover:bg-gray-50 ${
                    row.isCareStartYear ? 'bg-primary/5' : 'bg-white'
                  }`}
                >
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className={`whitespace-nowrap px-3 py-2 align-top ${
                        col.sticky ? 'sticky left-0 z-10 bg-inherit' : ''
                      }`}
                    >
                      {col.render(row, prev, projection.terms)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}
